import { useEffect, useState } from 'react';
import { Loader2, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { demoCopiesApi } from '@/lib/api/demoCopies';
import { useAuthStore } from '@/stores/authStore';
import { pageOf, trackViews } from './demoViewTracking';

function useInReadyCopy(): boolean {
  return useAuthStore((state) => {
    const fundId = state.activeFundId ?? state.user?.fundId ?? null;
    return state.isAuthenticated && state.workspaces.some((workspace) => workspace.fundId === fundId && workspace.demoCopy?.status === 'ready');
  });
}

function TellUs(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await demoCopiesApi.sendFeedback(pageOf(window.location), text.trim());
      setText('');
      setOpen(false);
      toast('Thanks, we got it.');
    } catch {
      setError('Could not send it. Try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="fixed bottom-4 left-1/2 z-30 inline-flex h-8 -translate-x-1/2 items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 text-[13px] font-medium text-slate-700 shadow-sm hover:bg-slate-50">
        <MessageSquare aria-hidden className="h-3.5 w-3.5" />
        Tell us
      </button>
      <Dialog open={open} onOpenChange={(next) => { if (!busy) setOpen(next); }}>
        <DialogContent className="max-w-md p-0">
          <form className="space-y-4 p-6" onSubmit={(event) => { event.preventDefault(); void send(); }}>
            <DialogHeader>
              <DialogTitle>Tell us</DialogTitle>
              <DialogDescription>What is missing, confusing or great on this page? It goes straight to the Lionshare team.</DialogDescription>
            </DialogHeader>
            <Textarea aria-label="Your note" rows={5} value={text} onChange={(event) => setText(event.target.value)} maxLength={2_000} />
            {error && <p className="text-[13px] text-red-600">{error}</p>}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" disabled={busy} onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !text.trim()}>{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Send</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** In a person's ready private copy: records which pages are on screen and for how long, and offers Tell us. Nothing elsewhere. */
export function DemoCopyTracker(): JSX.Element | null {
  const inCopy = useInReadyCopy();
  const activeFundId = useAuthStore((state) => state.activeFundId);
  // Every beat names the copy it was counted in, the last one sent after switching away too.
  useEffect(() => (inCopy && activeFundId ? trackViews((beat) => demoCopiesApi.recordView(beat, activeFundId)) : undefined), [inCopy, activeFundId]);
  return inCopy ? <TellUs /> : null;
}
